import {
  TcsDocumentPresignResponseSchema,
  TcsDocumentRowSchema,
  type CreateTcsDocumentRequest,
  type MarkTcsInvoicePaidRequest,
  type TcsDocumentPresignRequest,
  type TcsDocumentPresignResponse,
  type TcsDocumentRow,
} from '@utazzo/shared';
import { z } from 'zod';

import { MAX_TCS_DOC_BYTES, type CreatedId } from './tcs-challans.api';

import { apiClient } from '@/lib/api-client';

/**
 * API client for per-invoice TCS (`/tcs-invoices`): the paid flag and the TCS
 * certificate, both independent of any combined challan.
 */

const CreatedIdSchema = z.object({ id: z.string().min(1) });

export const markInvoicePaid = async (
  invoiceId: string,
  body: MarkTcsInvoicePaidRequest,
): Promise<void> => {
  await apiClient.patch(`/tcs-invoices/${invoiceId}/paid`, body);
};

export const listCertificates = async (invoiceId: string): Promise<TcsDocumentRow[]> => {
  const { data } = await apiClient.get<unknown>(`/tcs-invoices/${invoiceId}/certificates`);
  return z.array(TcsDocumentRowSchema).parse(data);
};

export const presignCertificate = async (
  invoiceId: string,
  body: TcsDocumentPresignRequest,
): Promise<TcsDocumentPresignResponse> => {
  const { data } = await apiClient.post<unknown>(
    `/tcs-invoices/${invoiceId}/certificates/presign`,
    body,
  );
  return TcsDocumentPresignResponseSchema.parse(data);
};

export const createCertificate = async (
  invoiceId: string,
  body: CreateTcsDocumentRequest,
): Promise<CreatedId> => {
  const { data } = await apiClient.post<unknown>(`/tcs-invoices/${invoiceId}/certificates`, body);
  const { id } = CreatedIdSchema.parse(data);
  return { id };
};

export const deleteCertificate = async (invoiceId: string, docId: string): Promise<void> => {
  await apiClient.delete(`/tcs-invoices/${invoiceId}/certificates/${docId}`);
};

/**
 * Three-stage upload (presign -> raw PUT to S3 -> create row), identical to the
 * challan-document flow. The TCS certificate is always kind CERTIFICATE.
 */
export const uploadCertificate = async (invoiceId: string, file: File): Promise<CreatedId> => {
  if (file.size > MAX_TCS_DOC_BYTES) throw new Error(`${file.name} is larger than 25 MB`);
  const contentType = file.type || 'application/octet-stream';
  const presigned = await presignCertificate(invoiceId, {
    fileName: file.name,
    contentType,
    kind: 'CERTIFICATE',
  });
  const put = await fetch(presigned.uploadUrl, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': contentType },
  });
  if (!put.ok) throw new Error(`Upload failed (${put.status}): ${put.statusText}`);
  return createCertificate(invoiceId, {
    key: presigned.key,
    fileName: file.name,
    contentType,
    sizeBytes: file.size,
    kind: 'CERTIFICATE',
  });
};
